import { useState, useEffect, useMemo } from 'react'

import cn from 'classnames'

import { checkSingleValue, tr, usePolling } from 'mmfintech-commons'
import { globalSettings, TwoFactorTypes, useChallengeRequest } from 'mmfintech-checkout-commons'

import { DurationTimer } from '../../../components'
import { Button, ErrorDisplay } from 'mmfintech-portal-commons'
import { OtpResendMessage, PaymentContainer } from '../../CheckoutPayment/CheckoutPaymentCore.styled'

import type { TwoFactorType } from 'mmfintech-checkout-commons/types'

const getOptTitle = (twoFactorType: TwoFactorType) => {
  switch (twoFactorType) {
    case TwoFactorTypes.SMS:
      return 'SMS verification'

    case TwoFactorTypes.EMAIL:
      return 'Email verification'

    case TwoFactorTypes.TOTP:
      return 'Google Authenticator verification'

    case TwoFactorTypes.SMART_ID:
      return 'Smart ID verification'

    default:
      return 'Email verification'
  }
}

const getOptSubtitle = (twoFactorType: TwoFactorType) => {
  switch (twoFactorType) {
    case TwoFactorTypes.SMS:
      return tr('CHECKOUT.PAYMENT.OTP_SUBTITLE', "Please, enter the verification code that we've sent to:")

    case TwoFactorTypes.EMAIL:
      return tr('CHECKOUT.PAYMENT.OTP_SUBTITLE', "Please, enter the verification code that we've sent to:")

    case TwoFactorTypes.TOTP:
      return tr('CHECKOUT.PAYMENT.OTP_SUBTITLE_TOTP', 'Please, enter the verification code:')

    case TwoFactorTypes.SMART_ID:
      return tr(
        'CHECKOUT.PAYMENT.OTP_SUBTITLE_SMART_ID',
        'We’ve sent a notification to your device. Please check to verify it’s you.'
      )

    default:
      return tr('CHECKOUT.PAYMENT.OTP_SUBTITLE', "Please, enter the verification code that we've sent to:")
  }
}

interface WalletCheckoutOtpProps {
  onSuccess: (response: any) => void
  onCancel?: any
  wallet?: boolean
}

export const WalletCheckoutOtp = ({ onSuccess, onCancel, wallet }: WalletCheckoutOtpProps) => {
  const [expired, setExpired] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const [verificationCode, setVerificationCode] = useState('')

  const {
    challenge,
    resendError,
    resendFetching,
    solveError,
    solveFetching,
    smartIdPolling,

    resendCode,
    resetChallenge,
    solveChallenge,
    completeSmartIdChallenge
  } = useChallengeRequest(wallet)

  const { challengeId, sentTo, twoFactorType, otpLength } = challenge || {}

  const pool = usePolling(2000, () => completeSmartIdChallenge(challengeId, onSuccess, startPollingAgain))

  const startPollingAgain = () => {
    pool.start()
  }

  const codeSize = useMemo(() => (otpLength > 0 ? otpLength : globalSettings.otpCodeLength), [otpLength])

  const handleChange = ({ target }) => {
    const newValue = checkSingleValue(target.value, { validation: 'numeric' })
    setVerificationCode(newValue)
  }

  useEffect(() => {
    if (twoFactorType === TwoFactorTypes.SMART_ID) {
      completeSmartIdChallenge(challengeId, onSuccess)
    }
  }, [twoFactorType])

  useEffect(() => {
    if (twoFactorType === TwoFactorTypes.SMART_ID && smartIdPolling) {
      startPollingAgain()
    }
  }, [twoFactorType, smartIdPolling])

  const handleResend = () => {
    resendCode(challengeId, () => {
      setAttempts(0)
      setVerificationCode('')
    })
  }

  useEffect(() => {
    if (verificationCode.length === codeSize && attempts < globalSettings.otpMaxAttempts) {
      setAttempts(attempts + 1)
      solveChallenge(challengeId, verificationCode, onSuccess)
    }
    // eslint-disable-next-line
  }, [verificationCode])

  useEffect(() => {
    return () => {
      resetChallenge()
    }
  }, [])

  return (
    <PaymentContainer className='vertically-centered' data-test='wallet-checkout-otp'>
      <div className='otp-title'>{getOptTitle(twoFactorType)}</div>
      <div className='otp-subtitle'>
        {getOptSubtitle(twoFactorType)}
        {sentTo && <span>{sentTo}</span>}
      </div>

      <ErrorDisplay error={solveError} />

      {resendFetching ? null : resendError || twoFactorType === TwoFactorTypes.SMART_ID ? (
        <>
          <ErrorDisplay error={resendError} />

          {onCancel && (
            <Button
              type='button'
              color='secondary'
              text={tr('CHECKOUT.BUTTONS.CANCEL', 'Cancel')}
              onClick={onCancel}
              data-test='button-cancel'
            />
          )}
        </>
      ) : (
        <>
          <div className='text-center'>
            <input
              id='otp'
              type='tel'
              className={cn({
                complete: verificationCode?.length === codeSize,
                wrong: verificationCode?.length === codeSize && !!solveError
              })}
              autoComplete='off'
              onChange={handleChange}
              value={verificationCode}
              maxLength={codeSize}
              autoFocus
              placeholder={tr('CHECKOUT.OTP.ENTER_CODE', 'Enter Code')}
              disabled={expired || attempts >= globalSettings.otpMaxAttempts}
              data-test='tel-input'
            />
          </div>
          {twoFactorType !== TwoFactorTypes.TOTP && (
            <OtpResendMessage>
              {attempts < globalSettings.otpMaxAttempts ? (
                <>
                  {tr('CHECKOUT.OTP.HAVENT_RECEIVE', "Haven't received it?")}
                  <span onClick={handleResend}>{tr('CHECKOUT.OTP.RESEND_CODE', 'Re-send code')}</span>
                </>
              ) : (
                tr('CHECKOUT.OTP.NO_MORE_ATTEMPTS', 'No more verification attempts.')
              )}
            </OtpResendMessage>
          )}

          <div className='mt-3'>
            {typeof onCancel === 'function' && (
              <Button
                type='button'
                color='primary'
                text={tr('CHECKOUT.BUTTONS.CANCEL', 'Cancel')}
                disabled={solveFetching}
                onClick={onCancel}
                data-test='button-cancel'
              />
            )}
          </div>
        </>
      )}

      {attempts < globalSettings.otpMaxAttempts && !resendFetching && <DurationTimer setCustomExpired={setExpired} />}
    </PaymentContainer>
  )
}
